import type { ShareVoiceSidebarFilter } from "@/data-access/share-of-voice/queries/shareOfVoice-sidebar-filter-queries";
import { derive } from "@/lib/derive-zustand";
import type { Category } from "@/types/shared";
import { useStore } from "zustand";
import {
  FILTER_CATEGORY_TO_CATEGORY_MAP,
  SHARE_OF_VOICE_FILTER_ORDER,
} from "./shareOfVoice-filters.helpers";
import useShareOfVoiceSidebarFilters from "./shareOfVoice-filters.slice";

export type GroupedSegmentItem = ShareVoiceSidebarFilter[0] & {
  isLoadingCountAndPercentage: boolean;
};

export type GroupedSegmentsByCategory = Record<string, GroupedSegmentItem[]>;

/**
 * Groups share of voice sidebar segments by category label.
 * Categories are pre-initialized in display order for consistent object key ordering.
 */
const deriveGroupedSegments = derive<GroupedSegmentsByCategory>((get) => {
  const { sidebarFilters } = get(useShareOfVoiceSidebarFilters);

  // Initialize in display order by flattening the category mapping
  const grouped: GroupedSegmentsByCategory = {};

  for (const filterCategory of SHARE_OF_VOICE_FILTER_ORDER) {
    const categories = FILTER_CATEGORY_TO_CATEGORY_MAP[filterCategory] || [];
    for (const category of categories) {
      grouped[category] = [];
    }
  }

  // Populate with data - only include categories in our configuration
  for (const item of sidebarFilters) {
    const { categoryLabel } = item;

    // Skip categories that aren't in our predefined configuration
    if (!grouped[categoryLabel]) {
      continue;
    }

    grouped[categoryLabel].push(item);
  }

  return grouped;
});

export const useGroupedSegments = () => useStore(deriveGroupedSegments);

/**
 * Hook to use segments for a specific category.
 * This works standalone - you don't need to call useGroupedSegments() separately.
 * @param category - The category label to filter by
 * @returns {GroupedSegmentItem[]} Array of segments for the specified category
 */
export const useGroupedSegmentsByCategory = (
  category: Category,
): GroupedSegmentItem[] => {
  const allGrouped = useStore(
    deriveGroupedSegments,
  ) as GroupedSegmentsByCategory;
  return allGrouped[category] || [];
};
